import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import React from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import Link from "./ui/RouterLink";
import useIdle from "./useIdle";
import { IDLE_TIMEOUT } from "./utils";
import LandingPage from "./pages/LandingPage";
import BrowsePage from "./pages/BrowsePage";
import AlbumDetails from "./pages/AlbumDetails";
import ArtistPage from "./pages/ArtistPage";
import AdminHubPage from "./pages/AdminHubPage";
import SetList from "./pages/SetList";
import SetDetails from "./pages/SetDetails";
import AddTrackToSet from "./pages/AddTrackToSet";
import AddSetTrack from "./pages/AddSetTrack";
import Menu from "./components/Menu";
import ImportNotice from "./components/ImportNotice";
import useUserProfile from "./hooks/useUserProfile";
import useCollectionImport from "./hooks/useCollectionImport";
import useDarkMode from "./hooks/useDarkMode";
import RouteErrorBoundary from "./RouteErrorBoundary";
import ArchivedAlbums from "./pages/ArchivedAlbums";
import UserSettings from "./pages/UserSettings";
import useCollectionSettings from "./hooks/useCollectionSettings";
import AppTheme from "./ui/AppTheme";
import DialogProvider from "./ui/DialogProvider";
function AlbumDetailsRoute({ role, username, settings }) {
  const { albumId } = useParams();
  return (
    <AlbumDetails
      albumId={albumId}
      role={role}
      username={username}
      settings={settings}
    />
  );
}
function ArtistPageRoute() {
  const { artistId } = useParams();
  const navigate = useNavigate();
  return (
    <ArtistPage
      artistId={artistId}
      onSelectAlbum={(id) => navigate(`/album/${id}`)}
    />
  );
}
export default function App() {
  const session = useUserProfile();
  const collectionSettings = useCollectionSettings(session);
  const [darkMode, setDarkMode] = useDarkMode(collectionSettings);
  return (
    <AppTheme darkMode={darkMode}>
      <DialogProvider>
        <AppContent
          session={session}
          collectionSettings={collectionSettings}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
        />
      </DialogProvider>
    </AppTheme>
  );
}
function AppContent({ session, collectionSettings, darkMode, setDarkMode }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const idle = useIdle(IDLE_TIMEOUT);
  const { username, role } = session;
  const {
    imported,
    importStatus,
    startingImport,
    importMessage,
    importNoticeJobId,
    importNoticeStatus,
    dismissingImport,
    dismissalError,
    dismissImport,
    collectionRevision,
    startImport,
    cancelImport,
  } = useCollectionImport(username, idle, menuOpen);
  const navigate = useNavigate();
  const location = useLocation();
  if (session.status === "loading")
    return <p role="status">Checking session…</p>;
  if (session.status === "error")
    return (
      <Alert role="alert" severity="error">
        Could not initialize your session: {session.error.message}
        <Button className="btn" onClick={session.refreshProfile}>
          Retry session
        </Button>
      </Alert>
    );
  return (
    <div className="container">
      <header>
        <div className="header-main">
          <div className="header-left">
            <Link to="/">
              <img
                src={
                  darkMode
                    ? "/bpm-binder-graffiti-text-white.svg"
                    : "/bpm-binder-graffiti-text.svg"
                }
                alt="BPM Binder"
                className="logo"
              />
            </Link>
          </div>
          <div className="actions">
            {username && (
              <Menu
                open={menuOpen}
                setOpen={setMenuOpen}
                onImport={() => startImport(username)}
                canImport={!!username}
                role={role}
                importStatus={importStatus}
                startingImport={startingImport}
                onCancelImport={cancelImport}
                signingOut={session.signingOut}
                onSignOut={async () => {
                  if (await session.signOut()) {
                    setMenuOpen(false);
                    navigate("/", { replace: true });
                  }
                }}
              />
            )}
          </div>
        </div>
      </header>

      {session.signOutError && (
        <Alert role="alert" severity="error">
          Could not sign out: {session.signOutError} Try Sign Out again.
        </Alert>
      )}
      {importMessage && (
        <ImportNotice
          key={`${username}:${importNoticeJobId ?? importMessage}`}
          message={importMessage}
          modal={importNoticeStatus === "completed"}
          onDismiss={
            importNoticeJobId != null
              ? () => dismissImport(importNoticeJobId)
              : undefined
          }
          saving={dismissingImport}
          error={dismissalError}
        />
      )}
      <main id="main-content">
        <RouteErrorBoundary
          resetKey={`${location.pathname}:${username}:${role}`}
        >
          <Routes key={`${username}:${role}`}>
            <Route
              path="/"
              element={
                username ? (
                  <BrowsePage
                    collectionRevision={collectionRevision}
                    onSelect={(id, track) =>
                      navigate(`/album/${id}${track ? `?track=${track}` : ""}`)
                    }
                    onArtistSelect={(id) => navigate(`/artist/${id}`)}
                    start={imported}
                    showImportPrompt={!imported}
                    onImport={() => startImport(username)}
                    importStatus={importStatus}
                    startingImport={startingImport}
                    onCancelImport={cancelImport}
                    signingOut={session.signingOut}
                    onSignOut={async () => {
                      if (await session.signOut()) {
                        setMenuOpen(false);
                        navigate("/", { replace: true });
                      }
                    }}
                  />
                ) : (
                  <LandingPage onImport={startImport} username={username} />
                )
              }
            />
            <Route
              path="/album/:albumId"
              element={
                <AlbumDetailsRoute
                  role={role}
                  username={username}
                  settings={collectionSettings}
                />
              }
            />
            <Route
              path="/archived"
              element={
                username ? (
                  <ArchivedAlbums settings={collectionSettings} />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="/artist/:artistId" element={<ArtistPageRoute />} />
            <Route
              path="/settings"
              element={
                username ? (
                  <UserSettings
                    key={username}
                    settings={collectionSettings}
                    darkMode={darkMode}
                    setDarkMode={setDarkMode}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="/sets" element={<SetList />} />
            <Route path="/sets/:setId" element={<SetDetails />} />
            <Route path="/add-to-set" element={<AddTrackToSet />} />
            <Route path="/add-track" element={<AddSetTrack />} />
            <Route
              path="/admin"
              element={<AdminHubPage session={session} idle={idle} />}
            />
            <Route
              path="/admin/artist-name-review"
              element={<Navigate to="/admin?tab=artist-name-review" replace />}
            />
            <Route
              path="*"
              element={
                <section>
                  <h2>Page not found</h2>
                  <Link to="/">Return to collection</Link>
                </section>
              }
            />
          </Routes>
        </RouteErrorBoundary>
      </main>
    </div>
  );
}
